// @flow
import { Backbone } from "extensions/backbone";
import { captureBreadcrumb } from "extensions/sentry";

import {
    UnspecifiedCurrency,
    DateRangeError
} from "common/helpers/errors";

import { toQueryString } from "common/utils";

export type DateType = {|
    start: string,
    end: string,
    datetype?: "activity" | "settlement"
|};

type ParamsObjectType = { [string]: string | boolean | number };

export const _parameterized = {
    reasonCodes: {
        RANGE: "exceededRange", // Exceeded maximum date range
        UNKNOWN: "unknown", // Unknown error
        API_ERROR: "api_error", // There was an api error that failed the request
        CANCELED: "canceled" // Request is canceled client-side (aborted, page refresh, etc.)
    },
    
    resetParams() {
        this.queryParams = {};
    },
    
    getQueryParams(item: string) {
        const queryParams = this.getAllParameters();
        
        if (item) {
            if (Object.prototype.hasOwnProperty.call(queryParams, item)) {
                return queryParams[item];
            }
            return void 0;
        }
        
        return queryParams;
    },
    
    setQueryParam(key: string | ParamsObjectType, val?: mixed) {
        if (typeof key === "undefined") {
            return this;
        }
        
        // Handle both `"key", value` and `{key: value}` -style arguments.
        let attrs;
        if (typeof key === "object") {
            attrs = key;
        } else {
            (attrs = {})[key] = val;
        }
        
        this.queryParams = Object.assign(this.queryParams, attrs);
        
        return this;
    },
    
    // Ask for first page of the data on some methods, i.e. filter, sort, getForRange...
    _setQueryParamsAndFetch(params: ParamsObjectType, resetPage: boolean) {
        const context = this.isProxy ? this.parent : this;
        this._setQueryParams(params, resetPage);
        return context.fetch();
    },
    
    _setQueryParams(params: ParamsObjectType, resetPage: boolean) {
        const context = this.isProxy ? this.parent : this;
        
        if (resetPage) {
            params = Object.assign(params, {
                page: 1
            });
        }
        
        if (typeof context.reset === "function") {
            context.reset();
        }
        
        context.setQueryParam(params);
    },
    
    getForDateRange(range: DateType, fetchImmediately: boolean = true) {
        if (!Object.prototype.hasOwnProperty.call(range, "start") || !Object.prototype.hasOwnProperty.call(range, "end")) {
            throw new DateRangeError({
                message: "Expected date range as an argument"
            });
        }
        
        if (fetchImmediately) {
            return this._setQueryParamsAndFetch(range, true);
        }
        
        return this._setQueryParams(range, true);
    },
    
    getForCurrency(currency: string) {
        if (!currency) {
            throw new UnspecifiedCurrency({
                message: "Unspecified currency"
            });
        }
        return this._setQueryParamsAndFetch({
            currency
        }, true);
    },
    
    getAllParameters() {
        return { ...this.queryParams };
    },
    
    /**
     * Filter request parameters.
     * Don't include params where key is empty. false is fine as we use to return stl_amount
     * @param params
     * @returns {*}
     */
    filterParameters(params: ParamsObjectType): ParamsObjectType {
        return Object.entries(params).reduce((memo, [key, value]) => {
            if (value !== "" && typeof value !== "undefined") {
                memo[key] = value;
            }
            return memo;
        }, {});
    },
    
    generateURLQueryParams() {
        const allCallParams = this.getAllParameters();
        return toQueryString(this.filterParameters(allCallParams));
    },
    
    getConstructorClass() {
        let ConstructorClass;
        if (this instanceof Backbone.Collection) {
            ConstructorClass = Backbone.Collection;
        } else if (this instanceof Backbone.Model) {
            ConstructorClass = Backbone.Model;
        } else {
            throw new TypeError("Unknown constructor class");
        }
        
        return ConstructorClass;
    },
    
    clone() {
        const ConstructorClass = this.getConstructorClass();
        
        // Apply "regular" clone and then set our additional state properties
        const clone = ConstructorClass.prototype.clone.apply(this, arguments);
        
        clone.fetched = this.fetched;
        clone.fetchPromise = this.fetchPromise;
        clone.queryParams = this.queryParams;
        
        return clone;
    },
    
    fetch(extraOptions = {}) {
        // Abort previous request if there is one
        if (this.fetchPromise) {
            this.fetchPromise.cancel();
        }
        
        const options = {
            reset: true,
            data: this.generateURLQueryParams(),
            ...extraOptions
        };
        
        // Before sending the request to fetch new data trigger before:fetched event
        this.fetched = false;
        this.trigger("before:fetched", this);
        
        const success = options.success;
        const error = options.error;
        
        options.success = (resp) => {
            if (success) {
                success(this, resp, options);
            }
            
            // If a structure has been fetched for the first time,
            // trigger fetched event and set status to `fetched`
            this.fetched = true;
            this.trigger("fetched", this, resp, options);
            this.fetchPromise = null;
        };
        
        options.error = (structure, response, opts) => {
            if (error) {
                error(structure, response, opts);
            }
            
            this.fetched = false;
            
            let reason;
            const status = response.status;
            if (status === 0) {
                reason = this.reasonCodes.CANCELED;
            } else if (status && status > 299) {
                reason = this.reasonCodes.API_ERROR;
            } else {
                reason = this.reasonCodes.UNKNOWN;
            }
            
            this.trigger("fetchError", { reason, status });
            this.fetchPromise = null;
        };
        
        const ConstructorClass = this.getConstructorClass();

        this.fetchPromise = ConstructorClass.prototype.fetch.call(this, options);
        
        // Overload abort function to capture a breadcrumb in Sentry
        const fnAbort = this.fetchPromise.cancel;
        this.fetchPromise.cancel = function() {
            captureBreadcrumb({
                message: "Aborting active fetch request",
                category: "parameterized",
                data: {
                    url: this.fetchPromise?.url
                }
            });
            return fnAbort.apply(this, arguments);
        };
        
        return this.fetchPromise;
    },
    
    /**
     * Check if this model or collection has been fetched (we made a call to the server that completed with 2xx status)
     * @returns {boolean}
     */
    isFetched(): boolean {
        return !!this.fetched;
    },
    
    /**
     * Get sort and order params to data structure from passed in table columns
     * @param {Object} [options = {}]
     * @param {Array} [options.columns = []] Table columns
     */
    getSortParamsFromTableColumns(columns: Array<Object> = []) {
        const activeSortedColumn = columns.find((c) => c.sortOptions && ["1", "-1"].includes(c.sortOptions.active));
        
        if (activeSortedColumn) {
            const attribute = typeof activeSortedColumn.attribute === "string" ? activeSortedColumn.attribute : activeSortedColumn.sortOptions.attribute;
            const active = activeSortedColumn.sortOptions.active;
            
            return {
                sort: attribute,
                order: active
            };
        }
        
        return {};
    },
    
    /**
     * Set sort and order params to data structure from passed in table columns
     * @param {Object} [options = {}]
     * @param {Array} [options.columns = []] Table columns
     */
    setSortParamsFromTableColumns({ columns = [] }: { columns: Array<Object> } = {}) {
        const params = this.getSortParamsFromTableColumns(columns);
        this.setQueryParam(params);
    }
};
